/**
 * Short-lived, owner-bound storage for unsaved form fields that must survive a
 * single forced page reload (stale deployment / missing chunk recovery).
 *
 * Rules enforced here, not by callers:
 *  - a draft is written only for an authenticated identity (#157 ownership) and
 *    can be read back only by that same identity,
 *  - a draft expires (default 30 minutes) and is consumed by the first read,
 *  - nothing that looks like a credential, bearer token, invitation token or
 *    signed URL is ever written; the whole save is refused instead and the
 *    caller reports the loss explicitly,
 *  - every storage access is guarded, so unavailable/throwing storage degrades
 *    to "not saved" instead of crashing recovery.
 */
import type { SessionLifecycle } from './sessionLifecycle';

export type DraftValue = string | number | boolean | null | DraftValue[] | { [key: string]: DraftValue };
export type DraftFields = { [key: string]: DraftValue };

export type DraftSaveResult = 'saved' | 'unavailable' | 'sensitive' | 'anonymous';

export interface DraftStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  key?(index: number): string | null;
  readonly length?: number;
}

export interface DraftStoreOptions {
  /** Prefix that keeps surfaces apart, e.g. "mbbe-admin". */
  namespace: string;
  /** Opaque uid of the identity that currently owns the page; null when signed out. */
  getUid: () => string | null;
  storage?: () => DraftStorage | null;
  ttlMs?: number;
  now?: () => number;
}

export interface DraftStore {
  save(key: string, fields: DraftFields): DraftSaveResult;
  /** Returns and removes the draft when it belongs to the current identity and has not expired. */
  take(key: string): DraftFields | null;
  discard(key: string): void;
  /** Removes expired drafts of this namespace. */
  prune(): void;
  /**
   * Removes every draft of this namespace that does not belong to the current identity (all of them
   * when signed out), together with expired ones.
   */
  discardForeign(): void;
}

export const DRAFT_RECOVERY_TTL_MS = 30 * 60 * 1000;

const SECRET_FIELD_NAME = /(password|passwd|secret|token|authorization|bearer|signature|credential|api[-_]?key)/i;
const SIGNED_URL_PARAM =
  /[?&](x-amz-signature|x-amz-credential|x-amz-security-token|x-goog-signature|x-goog-credential|signature|sig|token|access_token|id_token|key|invite|invitation)=/i;
const BEARER_VALUE = /\bbearer\s+[a-z0-9._~+/-]{12,}/i;
const JWT_VALUE = /\beyJ[a-zA-Z0-9_-]{8,}\.[a-zA-Z0-9_-]{8,}\.[a-zA-Z0-9_-]{8,}/;

/** True when a string looks like a credential, bearer token or signed URL. */
export function looksSensitive(value: string): boolean {
  if (BEARER_VALUE.test(value) || JWT_VALUE.test(value)) return true;
  for (const match of value.matchAll(/https?:\/\/[^\s)"'<>]+/gi)) {
    if (SIGNED_URL_PARAM.test(match[0])) return true;
  }
  return false;
}

function containsSensitive(value: DraftValue, fieldName = ''): boolean {
  if (typeof value === 'string') return looksSensitive(value);
  if (Array.isArray(value)) return value.some((item) => containsSensitive(item, fieldName));
  if (value && typeof value === 'object') {
    return Object.entries(value).some(
      ([name, item]) => SECRET_FIELD_NAME.test(name) || containsSensitive(item, name),
    );
  }
  return false;
}

function defaultStorage(): DraftStorage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

interface StoredDraft {
  ownerUid: string;
  savedAt: number;
  fields: DraftFields;
}

export function createDraftStore(options: DraftStoreOptions): DraftStore {
  const ttlMs = options.ttlMs ?? DRAFT_RECOVERY_TTL_MS;
  const now = options.now ?? Date.now;
  const prefix = `${options.namespace}:draft:`;
  const storage = (): DraftStorage | null => {
    try {
      return (options.storage ?? defaultStorage)();
    } catch {
      return null;
    }
  };

  const read = (store: DraftStorage, storageKey: string): StoredDraft | null => {
    const raw = store.getItem(storageKey);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as Partial<StoredDraft>;
      if (
        typeof parsed.ownerUid === 'string' &&
        typeof parsed.savedAt === 'number' &&
        parsed.fields &&
        typeof parsed.fields === 'object' &&
        !Array.isArray(parsed.fields)
      ) {
        return { ownerUid: parsed.ownerUid, savedAt: parsed.savedAt, fields: parsed.fields };
      }
    } catch {
      // unreadable draft: treated as missing
    }
    return null;
  };

  return {
    save(key, fields) {
      const uid = options.getUid();
      if (uid === null) return 'anonymous';
      if (containsSensitive(fields)) return 'sensitive';
      try {
        const store = storage();
        if (!store) return 'unavailable';
        const storageKey = prefix + key;
        const existing = read(store, storageKey);
        // Another account's unexpired draft is never overwritten.
        if (existing && existing.ownerUid !== uid && now() - existing.savedAt <= ttlMs) return 'unavailable';
        const record: StoredDraft = { ownerUid: uid, savedAt: now(), fields };
        store.setItem(storageKey, JSON.stringify(record));
        return 'saved';
      } catch {
        return 'unavailable';
      }
    },
    take(key) {
      try {
        const store = storage();
        if (!store) return null;
        const storageKey = prefix + key;
        const draft = read(store, storageKey);
        if (!draft) return null;
        if (now() - draft.savedAt > ttlMs) {
          store.removeItem(storageKey);
          return null;
        }
        if (draft.ownerUid !== options.getUid()) return null;
        store.removeItem(storageKey);
        return draft.fields;
      } catch {
        return null;
      }
    },
    discard(key) {
      try {
        const store = storage();
        if (!store) return;
        const storageKey = prefix + key;
        const draft = read(store, storageKey);
        if (draft && draft.ownerUid !== options.getUid()) return;
        store.removeItem(storageKey);
      } catch {
        // nothing to discard when storage is unavailable
      }
    },
    prune() {
      sweep(false);
    },
    discardForeign() {
      sweep(true);
    },
  };

  function sweep(dropForeign: boolean): void {
    try {
      const store = storage();
      if (!store || typeof store.length !== 'number' || !store.key) return;
      const uid = options.getUid();
      const doomed: string[] = [];
      for (let index = 0; index < store.length; index += 1) {
        const storageKey = store.key(index);
        if (!storageKey || !storageKey.startsWith(prefix)) continue;
        const draft = read(store, storageKey);
        if (!draft || now() - draft.savedAt > ttlMs || (dropForeign && draft.ownerUid !== uid)) {
          doomed.push(storageKey);
        }
      }
      doomed.forEach((storageKey) => store.removeItem(storageKey));
    } catch {
      // sweeping is best effort
    }
  }
}

/**
 * Keeps stored drafts from outliving the identity that wrote them (#157): expired drafts are pruned
 * once now, and whenever the session identity changes (sign-out, account switch) every draft that is
 * not owned by the new identity is removed. The initial anonymous scope is not a change, so a draft
 * kept for a reload is still there when the same account is restored by the freshly loaded page.
 * Re-entering the anonymous scope while already anonymous (a StrictMode unmount) changes nothing.
 */
export function bindDraftStoreToSession(lifecycle: Pick<SessionLifecycle, 'subscribe'>, drafts: DraftStore): () => void {
  drafts.prune();
  return lifecycle.subscribe((next, previous) => {
    // anonymous -> anonymous is not an identity change. React StrictMode ends the (still anonymous)
    // session while it simulates an unmount, and that must not wipe a draft kept for this very reload.
    if (next.uid === null && previous.uid === null) return;
    drafts.discardForeign();
  });
}
